import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const SdsIntelligenceSystemResult = ({result}) => {
  const categoryIcons = {
    document_metadata: { icon: 'FileText', color: 'text-blue-600', bg: 'bg-blue-100' },
    product_info: { icon: 'Package', color: 'text-purple-600', bg: 'bg-purple-100' },
    manufacturer: { icon: 'Factory', color: 'text-orange-600', bg: 'bg-orange-100' },
    hazards: { icon: 'Warning', color: 'text-red-600', bg: 'bg-red-100' },
    composition: { icon: 'Flask', color: 'text-green-600', bg: 'bg-green-100' },
    first_aid: { icon: 'FirstAid', color: 'text-pink-600', bg: 'bg-pink-100' },
    firefighting: { icon: 'Fire', color: 'text-red-600', bg: 'bg-red-100' },
    accidental_release: { icon: 'Spill', color: 'text-yellow-600', bg: 'bg-yellow-100' },
    handling_storage: { icon: 'Warehouse', color: 'text-indigo-600', bg: 'bg-indigo-100' },
    exposure_controls: { icon: 'Shield', color: 'text-cyan-600', bg: 'bg-cyan-100' },
    physical_chemical: { icon: 'Atom', color: 'text-teal-600', bg: 'bg-teal-100' },
    stability_reactivity: { icon: 'Flask', color: 'text-emerald-600', bg: 'bg-emerald-100' },
    toxicological_info: { icon: 'Skull', color: 'text-rose-600', bg: 'bg-rose-100' },
    ecological_info: { icon: 'Tree', color: 'text-lime-600', bg: 'bg-lime-100' },
    disposal_info: { icon: 'Trash', color: 'text-gray-600', bg: 'bg-gray-100' },
    transport_info: { icon: 'Truck', color: 'text-amber-600', bg: 'bg-amber-100' },
    regulatory_info: { icon: 'Scale', color: 'text-violet-600', bg: 'bg-violet-100' },
    pictograms: { icon: 'Image', color: 'text-fuchsia-600', bg: 'bg-fuchsia-100' },
    regional_compliance: { icon: 'Globe', color: 'text-sky-600', bg: 'bg-sky-100' }
  };

  const entityCategories = [
    'document_metadata',
    'product_info',
    'manufacturer',
    'hazards',
    'composition',
    'first_aid',
    'firefighting',
    'accidental_release',
    'handling_storage',
    'exposure_controls',
    'physical_chemical',
    'stability_reactivity',
    'toxicological_info',
    'ecological_info',
    'disposal_info',
    'transport_info',
    'regulatory_info',
    'pictograms',
    'regional_compliance'
  ];

  const formatEntityRow = (entity) => (
    <div className="mb-2 p-2 bg-base-200 rounded-lg">
      <div className="flex items-center justify-between">
        <span className="font-medium">{entity?.value}</span>
        <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
          {entity?.confidence * 100}%
        </span>
      </div>
      <div className="text-sm mt-1">
        <span className="text-base-content/70">Category: </span>
        <span className="capitalize">{entity.category}</span>
      </div>
      {entity?.language && (
        <div className="text-sm mt-1">
          <span className="text-base-content/70">Language: </span>
          <span>{entity.language}</span>
        </div>
      )}
      {entity?.normalized_value && (
        <div className="text-sm mt-1">
          <span className="text-base-content/70">Normalized: </span>
          <span>{entity.normalized_value}</span>
        </div>
      )}
      {entity?.mentions && entity?.mentions?.length > 0 && (
        <div className="text-sm mt-1">
          <span className="text-base-content/70">Mentions: </span>
          {entity?.mentions?.join(', ')}
        </div>
      )}
    </div>
  );

  const formatRelationship = (relationship) => (
    <div className="mb-2 p-2 bg-base-200 rounded-lg">
      <div className="flex items-center justify-between">
        <span className="font-medium">{relationship.relationship_type}</span>
        <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
          {relationship.confidence * 100}%
        </span>
      </div>
      <div className="text-sm mt-1">
        <span className="text-base-content/70">Subject: </span>
        <span>{relationship.subject}</span>
      </div>
      <div className="text-sm mt-1">
        <span className="text-base-content/70">Object: </span>
        <span>{relationship.object}</span>
      </div>
    </div>
  );

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Entity Categories */}
        <div className="space-y-4">
          {entityCategories?.map(category => {
            const entities = result?.entities?.[category] || [];
            if (entities.length === 0) return null;

            return (
              <div key={category} className="card bg-base-100 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`p-2 rounded-lg ${categoryIcons[category]?.bg || 'bg-base-200'}`}>
                      {PhosphorIcons[categoryIcons[category]?.icon] && 
                        React.createElement(PhosphorIcons[categoryIcons[category]?.icon], {
                          size: 20,
                          weight: "bold",
                          className: categoryIcons[category]?.color || 'text-base-content'
                        })
                      }
                    </div>
                    <h3 className="card-title text-lg capitalize">{category.replace(/_/g, ' ')}</h3>
                  </div>
                  <div className="space-y-2">
                    {entities?.map((entity, index) => (
                      <div key={index}>
                        {formatEntityRow(entity)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Metadata and Relationships */}
        <div className="space-y-4">
          {/* Document Classification */}
          {result?.document_classification && (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <h3 className="card-title text-lg mb-2">Document Classification</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Type:</span>
                    <span className="font-medium">{result.document_classification.document_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Framework:</span>
                    <span className="font-medium">{result.document_classification.regulatory_framework}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Confidence:</span>
                    <span className="font-medium">{result.document_classification.confidence * 100}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Completeness:</span>
                    <span className="font-medium">{result.document_classification.completeness * 100}%</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Relationships */}
          {result?.relationships && result.relationships.length > 0 && (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <h3 className="card-title text-lg mb-2">Relationships</h3>
                <div className="space-y-2">
                  {result.relationships.map((relationship, index) => (
                    <div key={index}>
                      {formatRelationship(relationship)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Compliance Assessment */}
          {result?.compliance_assessment && (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <h3 className="card-title text-lg mb-2">Compliance Assessment</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Framework:</span>
                    <span className="font-medium">{result.compliance_assessment.framework}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Completeness Score:</span>
                    <span className="font-medium">{result.compliance_assessment.completeness_score * 100}%</span>
                  </div>
                  {result.compliance_assessment.missing_elements?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-1">Missing Elements:</h4>
                      <ul className="list-disc list-inside text-sm">
                        {result.compliance_assessment.missing_elements.map((element, index) => (
                          <li key={index}>{element}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Risk Assessment */}
          {result?.risk_assessment && (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <h3 className="card-title text-lg mb-2">Risk Assessment</h3>
                <div className="space-y-4">
                  {result.risk_assessment.identified_hazards?.map((hazard, index) => (
                    <div key={index} className="p-2 bg-base-200 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{hazard.hazard}</span>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                          {hazard.confidence * 100}%
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                        <div>
                          <span className="text-base-content/70">Severity:</span>
                          <span className="ml-1">{hazard.severity}</span>
                        </div>
                        <div>
                          <span className="text-base-content/70">Likelihood:</span>
                          <span className="ml-1">{hazard.likelihood}</span>
                        </div>
                        <div>
                          <span className="text-base-content/70">Risk Level:</span>
                          <span className="ml-1">{hazard.risk_level}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="mt-2">
                    <span className="text-base-content/70">Protective Measures Adequacy:</span>
                    <span className="ml-1 font-medium">{result.risk_assessment.protective_measures_adequacy}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Metadata Summary */}
          {result?.metadata && (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <PhosphorIcons.ChartBar size={20} weight="bold" className="text-primary"/>
                  </div>
                  <h3 className="card-title text-lg">Summary</h3>
                </div>
                
                <div className="space-y-4">
                  {/* Entity Counts */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-2 rounded-lg bg-info/10">
                        <PhosphorIcons.ChartPie size={16} weight="bold" className="text-info"/>
                      </div>
                      <h4 className="font-medium">Entity Counts</h4>
                    </div>
                    <div className="text-sm">
                      <div className="flex justify-between mb-1">
                        <span>Total Entities:</span>
                        <span className="font-medium">{result?.metadata?.entity_counts?.total}</span>
                      </div>
                      {entityCategories.map(category => {
                        const count = result?.metadata?.entity_counts?.by_category?.[category];
                        if (count === undefined) return null;
                        return (
                          <div key={category} className="flex justify-between">
                            <span className="capitalize">{category.replace(/_/g, ' ')}:</span>
                            <span>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Confidence Scores */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-2 rounded-lg bg-success/10">
                        <PhosphorIcons.ChartLineUp size={16} weight="bold" className="text-success"/>
                      </div>
                      <h4 className="font-medium">Confidence Scores</h4>
                    </div>
                    <div className="text-sm">
                      <div className="flex justify-between mb-1">
                        <span>Average:</span>
                        <span className="font-medium">{result?.metadata?.confidence_summary?.average * 100}%</span>
                      </div>
                      {entityCategories.map(category => {
                        const confidence = result?.metadata?.confidence_summary?.by_category?.[category];
                        if (confidence === undefined) return null;
                        return (
                          <div key={category} className="flex justify-between">
                            <span className="capitalize">{category.replace(/_/g, ' ')}:</span>
                            <span>{confidence * 100}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Language Summary */}
                  {result?.metadata?.language_summary && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 rounded-lg bg-warning/10">
                          <PhosphorIcons.Translate size={16} weight="bold" className="text-warning"/>
                        </div>
                        <h4 className="font-medium">Language Summary</h4>
                      </div>
                      <div className="text-sm">
                        <div className="flex justify-between">
                          <span>Primary Language:</span>
                          <span>{result.metadata.language_summary.primary_language}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Confidence:</span>
                          <span>{result.metadata.language_summary.confidence * 100}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Translated Elements:</span>
                          <span>{result.metadata.language_summary.translated_elements}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SdsIntelligenceSystemResult;
